import _ from 'lodash';
import {createSelector} from 'reselect';

import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {ApplicationEntryPointTypesEnum} from '../../../models/api/applicationEntryPointModel';
import {AppState} from '../../appState';

/*
 * Selectors.
 */

export function getLocalApplications(state: AppState) {
  return state.data.localApplications;
}

export const getFlatLocalApplications = createSelector(getLocalApplications, (applications) =>
  _(applications).values().compact().value(),
);

export function makeGetLocalApplication(
  getLocalApplicationId: AppParametricSelector<any, number | undefined>,
) {
  return createSelector(
    [getLocalApplicationId, getLocalApplications],
    (localApplicationId, localApplications) => {
      if (!localApplicationId) {
        return undefined;
      }
      return localApplications[localApplicationId];
    },
  );
}

export function makeGetLocalApplicationsServers(
  getLocalApplicationId: AppParametricSelector<any, number | undefined>,
) {
  return createSelector(
    [getLocalApplicationId, getLocalApplications],
    (localApplicationId, localApplications) => {
      if (!localApplicationId) {
        return undefined;
      }
      return localApplications[localApplicationId]?.servers?.items;
    },
  );
}

export function makeGetLocalApplicationsServersAsyncStatus(
  getLocalApplicationId: AppParametricSelector<any, number | undefined>,
) {
  return createSelector(
    [getLocalApplicationId, getLocalApplications],
    (localApplicationId, localApplications) => {
      if (!localApplicationId) {
        return undefined;
      }
      return localApplications[localApplicationId]?.servers?.status;
    },
  );
}

export function makeGetLocalApplicationChannelType(
  getLocalApplicationId: AppParametricSelector<any, number | undefined>,
) {
  return createSelector(
    [getLocalApplicationId, getLocalApplications],
    (localApplicationId, localApplications) => {
      if (!localApplicationId) {
        return undefined;
      }
      return localApplications[localApplicationId]?.channelType?.value;
    },
  );
}

export function makeGetLocalApplicationsEntryPoints(
  getLocalApplicationId: AppParametricSelector<any, number | undefined>,
) {
  return createSelector(
    [getLocalApplicationId, getLocalApplications],
    (localApplicationId, localApplications) => {
      if (!localApplicationId) {
        return undefined;
      }
      return localApplications[localApplicationId]?.entryPoints?.items;
    },
  );
}

// There should only be a single inbox side panel entry point for an application.
export function makeGetLocalApplicationsInboxSidePanelEntryPoint(
  getLocalApplicationId: AppParametricSelector<any, number | undefined>,
) {
  return createSelector([makeGetLocalApplicationsEntryPoints(getLocalApplicationId)], (entryPoints) => {
    if (!entryPoints) {
      return undefined;
    }
    return entryPoints.filter((ep) => ep.type === ApplicationEntryPointTypesEnum.INBOX_SIDE_PANEL)[0];
  });
}

export function makeGetLocalApplicationsEntryPointAsyncStatus(
  getLocalApplicationId: AppParametricSelector<any, number | undefined>,
) {
  return createSelector(
    [getLocalApplicationId, getLocalApplications],
    (localApplicationId, localApplications) => {
      if (!localApplicationId) {
        return undefined;
      }
      return localApplications[localApplicationId]?.entryPoints?.status;
    },
  );
}
